import React, { useState, useEffect } from 'react';
import { getClients, getSettings, saveServiceAgreement, getServiceAgreement } from '../utils/storageUtils';
import { AGREEMENT_TEMPLATES, STANDARD_CLAUSES } from '../utils/agreement-templates';
import { searchNDISItems } from '../utils/ndisUtils';
import { REGIONS } from '../utils/ndis-pricing';
import { generateServiceAgreementPDF } from '../utils/serviceAgreementPDF';
import '../App.css';

export default function ServiceAgreements() {
  const [currentStep, setCurrentStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [expandedClauses, setExpandedClauses] = useState({});
  const [editingProvider, setEditingProvider] = useState(false);
  const [customClauseEditor, setCustomClauseEditor] = useState({ title: '', content: '' });
  const [formData, setFormData] = useState({
    participant: null,
    template: null,
    provider: {
      name: '',
      abn: '',
      contactPerson: '',
      phone: '',
      email: '',
      address: {
        street: '',
        suburb: '',
        state: '',
        postcode: ''
      },
      ndisRegistration: ''
    },
    supportItems: [],
    pricing: {
      region: 'metro',
      estimatedCosts: {
        weekly: 0,
        monthly: 0,
        annual: 0
      }
    },
    period: {
      startDate: '',
      endDate: '',
      ongoingUntilCancelled: false,
      duration: '12 months'
    },
    terms: {
      selectedClauses: [],
      customClauses: [],
      paymentTerms: 'Monthly in arrears',
      cancellationNotice: '14 days'
    }
  });

  const totalSteps = 7;

  // Load clients and prefill provider details on component mount
  useEffect(() => {
    const loadedClients = getClients();
    setClients(loadedClients);

    // Check if editing existing agreement
    const urlParams = new URLSearchParams(window.location.search);
    const editId = urlParams.get('edit');

    if (editId) {
      // Load existing agreement
      const existingAgreement = getServiceAgreement(editId);
      if (existingAgreement) {
        setFormData(existingAgreement);
        // Set current step based on completion
        if (existingAgreement.participant && existingAgreement.template) {
          setCurrentStep(7); // Go to review step for editing
        }
        return;
      }
    }

    // Prefill provider details from user settings (only for new agreements)
    const settings = getSettings();
    // Pre-select all recommended clauses
    const recommendedClauseIds = Object.entries(STANDARD_CLAUSES)
      .filter(([, clause]) => clause.required)
      .map(([clauseId]) => clauseId);
    if (settings) {
      setFormData(prev => ({
        ...prev,
        provider: {
          name: settings.businessName || '',
          abn: settings.abn || '',
          contactPerson: '', // User can fill this in if needed
          phone: settings.phone || '',
          email: settings.email || '',
          address: {
            street: settings.address || '',
            suburb: settings.suburb || '',
            state: settings.state || '',
            postcode: settings.postcode || ''
          },
          ndisRegistration: '' // User-specific field
        },
        terms: {
          ...prev.terms,
          selectedClauses: prev.terms.selectedClauses.length === 0 ? recommendedClauseIds : prev.terms.selectedClauses
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        terms: {
          ...prev.terms,
          selectedClauses: prev.terms.selectedClauses.length === 0 ? recommendedClauseIds : prev.terms.selectedClauses
        }
      }));
    }
  }, []);

  const stepTitles = [
    'Select Participant',
    'Choose Agreement Template',
    'Provider Details',
    'Support Details',
    'Pricing & Budget',
    'Terms & Clauses',
    'Review & Generate'
  ];

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const handleParticipantSelect = (clientId) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        participant: {
          id: selectedClient.id,
          name: selectedClient.name,
          ndisNumber: selectedClient.ndisNumber,
          dateOfBirth: selectedClient.dob,
          contact: {
            phone: selectedClient.phone || '',
            email: selectedClient.email || ''
          },
          address: {
            street: selectedClient.address || '',
            suburb: '',
            state: '',
            postcode: ''
          },
          nominee: {
            name: '',
            relationship: '',
            contact: ''
          }
        }
      }));
      // Auto-advance to next step after selection
      nextStep();
    }
  };

  const handleTemplateSelect = (templateId) => {
    const selectedTemplate = AGREEMENT_TEMPLATES[templateId];
    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        template: {
          id: templateId,
          name: selectedTemplate.name,
          description: selectedTemplate.description,
          category: selectedTemplate.category
        }
      }));
      // Auto-advance to next step after selection
      nextStep();
    }
  };

  const handleProviderChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      provider: {
        ...prev.provider,
        [field]: value
      }
    }));
  };

  const handleProviderAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      provider: {
        ...prev.provider,
        address: {
          ...prev.provider.address,
          [field]: value
        }
      }
    }));
  };

  const handleSupportSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length > 2) {
      const results = searchNDISItems(query).slice(0, 10); // Limit to top 10 results
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const searchSectionRef = React.useRef(null);

  const showAddSupportItemSearch = () => {
    setShowSearchBar(true);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const addSupportItem = (ndisItem) => {
    const maxRate = ndisItem.national_price || 0;
    const supportItem = {
      id: Date.now().toString(),
      itemNumber: ndisItem.item_code,
      name: ndisItem.description,
      unit: ndisItem.unit || 'hour',
      frequency: 'weekly',
      hoursPerSession: '',
      sessionsPerPeriod: 1,
      location: 'Participant home',
      otherLocation: '',
      specialRequirements: '',
      pricing: {
        maxRate: maxRate,
        hourlyRate: maxRate, // Pre-filled with max rate but fully editable
        region: 'metro'
      }
    };

    setFormData(prev => ({
      ...prev,
      supportItems: [supportItem, ...prev.supportItems]
    }));

    // Clear search and hide search bar
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowSearchBar(false);
  };

  const removeSupportItem = (itemId) => {
    setFormData(prev => {
      const newSupportItems = prev.supportItems.filter(item => item.id !== itemId);

      // If no support items left, show search bar again
      if (newSupportItems.length === 0) {
        setShowSearchBar(true);
      }

      return {
        ...prev,
        supportItems: newSupportItems
      };
    });
  };

  const updateSupportItem = (itemId, field, value) => {
    // Handle numeric fields properly - allow empty values but prevent negative numbers
    let processedValue = value;
    if (field === 'hoursPerSession' || field === 'sessionsPerPeriod') {
      if (value === '') {
        processedValue = '';
      } else {
        const numValue = parseFloat(value);
        processedValue = numValue < 0 ? 0 : numValue;
      }
    }

    setFormData(prev => ({
      ...prev,
      supportItems: prev.supportItems.map(item =>
        item.id === itemId
          ? { ...item, [field]: processedValue }
          : item
      )
    }));
  };

  const updateSupportItemPricing = (itemId, field, value) => {
    // Allow empty values but prevent negative numbers
    let processedValue = value;
    if (value === '') {
      processedValue = '';
    } else {
      const numValue = parseFloat(value);
      processedValue = numValue < 0 ? 0 : numValue;
    }

    setFormData(prev => ({
      ...prev,
      supportItems: prev.supportItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              pricing: {
                ...item.pricing,
                [field]: processedValue
              }
            }
          : item
      )
    }));
  };

  const calculateItemCosts = (item) => {
    const rate = parseFloat(item.pricing.hourlyRate) || 0;
    const hours = parseFloat(item.hoursPerSession) || 0;
    const sessions = parseFloat(item.sessionsPerPeriod) || 1;

    const costPerSession = rate * hours;

    let weeklyCost = 0;
    let monthlyCost = 0;
    let annualCost = 0;

    if (item.frequency === 'single') {
      // Single session - just the one-time cost
      weeklyCost = 0;
      monthlyCost = costPerSession;
      annualCost = costPerSession;
    } else if (item.frequency === 'daily') {
      weeklyCost = costPerSession * sessions * 7;
      monthlyCost = weeklyCost * 4.33; // Average weeks per month
      annualCost = weeklyCost * 52;
    } else if (item.frequency === 'weekly') {
      weeklyCost = costPerSession * sessions;
      monthlyCost = weeklyCost * 4.33;
      annualCost = weeklyCost * 52;
    } else if (item.frequency === 'fortnightly') {
      weeklyCost = (costPerSession * sessions) / 2;
      monthlyCost = weeklyCost * 4.33;
      annualCost = weeklyCost * 52;
    } else if (item.frequency === 'monthly') {
      weeklyCost = (costPerSession * sessions) / 4.33;
      monthlyCost = costPerSession * sessions;
      annualCost = monthlyCost * 12;
    }

    return {
      costPerSession,
      weeklyCost,
      monthlyCost,
      annualCost
    };
  };

  const calculateTotalCosts = () => {
    return formData.supportItems.reduce((totals, item) => {
      const costs = calculateItemCosts(item);
      return {
        weekly: totals.weekly + costs.weeklyCost,
        monthly: totals.monthly + costs.monthlyCost,
        annual: totals.annual + costs.annualCost
      };
    }, { weekly: 0, monthly: 0, annual: 0 });
  };

  const handleRegionChange = (region) => {
    const regionMultiplier = REGIONS[region]?.multiplier || 1.0;

    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        region: region
      },
      // Update all support item pricing with new regional multiplier
      supportItems: prev.supportItems.map(item => ({
        ...item,
        pricing: {
          ...item.pricing,
          region: region,
          hourlyRate: (item.pricing.maxRate || 0) * regionMultiplier
        }
      }))
    }));
  };

  const handleAgreementPeriodChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      period: {
        ...prev.period,
        [field]: value
      }
    }));
  };

  const handleTermsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      terms: {
        ...prev.terms,
        [field]: value
      }
    }));
  };

  const toggleClause = (clauseId) => {
    setFormData(prev => ({
      ...prev,
      terms: {
        ...prev.terms,
        selectedClauses: prev.terms.selectedClauses.includes(clauseId)
          ? prev.terms.selectedClauses.filter(id => id !== clauseId)
          : [...prev.terms.selectedClauses, clauseId]
      }
    }));
  };

  const toggleClauseExpansion = (clauseId) => {
    setExpandedClauses(prev => ({
      ...prev,
      [clauseId]: !prev[clauseId]
    }));
  };

  const addCustomClause = () => {
    if (customClauseEditor.title.trim() && customClauseEditor.content.trim()) {
      const newClause = {
        title: customClauseEditor.title.trim(),
        content: customClauseEditor.content.trim()
      };

      setFormData(prev => ({
        ...prev,
        terms: {
          ...prev.terms,
          customClauses: [...prev.terms.customClauses, newClause]
        }
      }));

      // Clear the editor
      setCustomClauseEditor({ title: '', content: '' });
    }
  };

  const removeCustomClause = (index) => {
    setFormData(prev => ({
      ...prev,
      terms: {
        ...prev.terms,
        customClauses: prev.terms.customClauses.filter((_, i) => i !== index)
      }
    }));
  };

  const generateAgreementNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `SA-${year}${month}${day}-${random}`;
  };

  const handleSaveDraft = () => {
    try {
      // Generate agreement number if not exists
      const agreementNumber = formData.agreementNumber || generateAgreementNumber();

      // Prepare agreement data for saving
      const agreementData = {
        ...formData,
        agreementNumber: agreementNumber,
        status: 'draft',
        lastModified: new Date().toISOString()
      };

      // Save to storage (automatically syncs to Firebase)
      const savedAgreement = saveServiceAgreement(agreementData);

      // Update form data with saved ID and agreement number
      setFormData(prev => ({
        ...prev,
        id: savedAgreement.id,
        agreementNumber: savedAgreement.agreementNumber
      }));

      alert('Draft saved successfully!');
    } catch (error) {
      alert('Error saving draft. Please try again.');
    }
  };

  const handleGeneratePDF = () => {
    try {
      // Get settings for provider info (fallback)
      const settings = getSettings();

      // Generate agreement number if not exists
      const agreementNumber = formData.agreementNumber || generateAgreementNumber();

      // Prepare agreement data for PDF
      const agreementData = {
        ...formData,
        agreementNumber: agreementNumber
      };

      // Generate PDF
      const pdf = generateServiceAgreementPDF(agreementData, settings);

      // Create filename
      const participantName = formData.participant?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Participant';
      const filename = `Service_Agreement_${agreementNumber}_${participantName}.pdf`;

      // Save PDF
      pdf.save(filename);

      // Save agreement to storage with "generated" status
      const agreementToSave = {
        ...agreementData,
        status: 'generated',
        generatedAt: new Date().toISOString(),
        pdfFilename: filename,
        lastModified: new Date().toISOString()
      };

      const savedAgreement = saveServiceAgreement(agreementToSave);

      // Update form data with saved ID and agreement number
      setFormData(prev => ({
        ...prev,
        id: savedAgreement.id,
        agreementNumber: savedAgreement.agreementNumber
      }));

      // Show success message
      alert(`Service Agreement PDF generated successfully!\nFile: ${filename}\nAgreement saved with ID: ${savedAgreement.id}`);

    } catch (error) {
      alert('Error generating PDF. Please check all required fields are completed.');
    }
  };

  return (
    <div className="service-agreements-page">
      <div className="page-header">
        <h1>NDIS Service Agreement Generator</h1>
        <p>Create professional NDIS service agreements with comprehensive legal clauses and pricing details</p>
      </div>

      {/* Step Indicator */}
      <div className="step-indicator">
        {stepTitles.map((title, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;

          return (
            <div
              key={stepNumber}
              className={`step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              onClick={() => goToStep(stepNumber)}
              style={{ cursor: 'pointer' }}
            >
              <div className="step-number">{stepNumber}</div>
              <div className="step-title">{title}</div>
            </div>
          );
        })}
      </div>

      {/* Form Content */}
      <div className="form-container">
        {currentStep === 1 && (
          <div className="step-content">
            <h2>Step 1: Select Participant</h2>
            <p>Choose an existing participant from your client database or create a new one.</p>

            {clients.length === 0 ? (
              <div className="empty-state">
                <h3>No Participants Found</h3>
                <p>You need to create at least one participant before creating a service agreement.</p>
                <div className="form-actions">
                  <button className="btn btn-primary" onClick={() => window.location.href = '/clients'}>
                    Go to Clients Page
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Select Participant</label>
                  <select
                    className="form-control"
                    value={formData.participant?.id || ''}
                    onChange={(e) => handleParticipantSelect(e.target.value)}
                  >
                    <option value="">Choose a participant...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} - NDIS: {client.ndisNumber || 'N/A'}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.participant && (
                  <div className="participant-preview">
                    <h3>Selected Participant</h3>
                    <div className="participant-details">
                      <div><strong>Name:</strong> {formData.participant.name}</div>
                      <div><strong>NDIS Number:</strong> {formData.participant.ndisNumber || 'Not provided'}</div>
                      <div><strong>Date of Birth:</strong> {formData.participant.dateOfBirth || 'Not provided'}</div>
                      <div><strong>Phone:</strong> {formData.participant.contact.phone || 'Not provided'}</div>
                      <div><strong>Email:</strong> {formData.participant.contact.email || 'Not provided'}</div>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn btn-secondary" onClick={() => window.location.href = '/clients'}>
                    Create New Participant
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-content">
            <h2>Step 2: Choose Agreement Template</h2>
            <p>Select the type of service agreement that best matches your support arrangement.</p>

            <div className="template-cards">
              {Object.entries(AGREEMENT_TEMPLATES).map(([templateId, template]) => (
                <div key={templateId} className="template-card">
                  <h3>{template.name}</h3>
                  <p>{template.description}</p>
                  <div className="template-category">
                    <span className="category-badge">{template.category}</span>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleTemplateSelect(templateId)}
                  >
                    Select Template
                  </button>
                </div>
              ))}
            </div>

            {formData.template && (
              <div className="template-preview">
                <h3>Selected Template</h3>
                <div className="template-details">
                  <div><strong>Name:</strong> {formData.template.name}</div>
                  <div><strong>Category:</strong> {formData.template.category}</div>
                  <div><strong>Description:</strong> {formData.template.description}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="step-content">
            <h2>Step 3: Provider Details</h2>
            <p>Confirm your provider details below. These are pulled from your settings.</p>

            {!editingProvider ? (
              <>
                <div className="provider-preview">
                  <div className="provider-preview-row">
                    <span className="provider-preview-label">Provider Name</span>
                    <span className="provider-preview-value">{formData.provider.name || '—'}</span>
                  </div>
                  <div className="provider-preview-row">
                    <span className="provider-preview-label">ABN</span>
                    <span className="provider-preview-value">{formData.provider.abn || '—'}</span>
                  </div>
                  {formData.provider.contactPerson && (
                    <div className="provider-preview-row">
                      <span className="provider-preview-label">Contact Person</span>
                      <span className="provider-preview-value">{formData.provider.contactPerson}</span>
                    </div>
                  )}
                  {formData.provider.ndisRegistration && (
                    <div className="provider-preview-row">
                      <span className="provider-preview-label">NDIS Registration</span>
                      <span className="provider-preview-value">{formData.provider.ndisRegistration}</span>
                    </div>
                  )}
                  <div className="provider-preview-row">
                    <span className="provider-preview-label">Phone</span>
                    <span className="provider-preview-value">{formData.provider.phone || '—'}</span>
                  </div>
                  <div className="provider-preview-row">
                    <span className="provider-preview-label">Email</span>
                    <span className="provider-preview-value">{formData.provider.email || '—'}</span>
                  </div>
                  <div className="provider-preview-row">
                    <span className="provider-preview-label">Address</span>
                    <span className="provider-preview-value">
                      {[formData.provider.address.street, formData.provider.address.suburb, formData.provider.address.state, formData.provider.address.postcode].filter(Boolean).join(', ') || '—'}
                    </span>
                  </div>
                </div>

                <div className="provider-preview-actions">
                  <button className="btn btn-primary" onClick={() => setEditingProvider(true)}>
                    Add/change my details
                  </button>
                  <button className="btn btn-success" onClick={() => { setCurrentStep(4); }}>
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>

            <div className="form-row">
              <div className="form-group">
                <label>Provider Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.name}
                  onChange={(e) => handleProviderChange('name', e.target.value)}
                  placeholder="Enter provider organization name"
                />
              </div>
              <div className="form-group">
                <label>ABN *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.abn}
                  onChange={(e) => handleProviderChange('abn', e.target.value)}
                  placeholder="12 345 678 901"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.contactPerson}
                  onChange={(e) => handleProviderChange('contactPerson', e.target.value)}
                  placeholder="Primary contact name"
                />
              </div>
              <div className="form-group">
                <label>NDIS Registration Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.ndisRegistration}
                  onChange={(e) => handleProviderChange('ndisRegistration', e.target.value)}
                  placeholder="4050000123"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.provider.phone}
                  onChange={(e) => handleProviderChange('phone', e.target.value)}
                  placeholder="(02) 1234 5678"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.provider.email}
                  onChange={(e) => handleProviderChange('email', e.target.value)}
                  placeholder="contact@provider.com.au"
                />
              </div>
            </div>

            <h3>Provider Address</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.address.street}
                  onChange={(e) => handleProviderAddressChange('street', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Suburb</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.address.suburb}
                  onChange={(e) => handleProviderAddressChange('suburb', e.target.value)}
                  placeholder="Suburb"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <select
                  className="form-control"
                  value={formData.provider.address.state}
                  onChange={(e) => handleProviderAddressChange('state', e.target.value)}
                >
                  <option value="">Select state...</option>
                  <option value="NSW">NSW</option>
                  <option value="VIC">VIC</option>
                  <option value="QLD">QLD</option>
                  <option value="WA">WA</option>
                  <option value="SA">SA</option>
                  <option value="TAS">TAS</option>
                  <option value="ACT">ACT</option>
                  <option value="NT">NT</option>
                </select>
              </div>
              <div className="form-group">
                <label>Postcode</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.provider.address.postcode}
                  onChange={(e) => handleProviderAddressChange('postcode', e.target.value)}
                  placeholder="2000"
                />
              </div>
            </div>

            <div className="provider-validation">
              {!formData.provider.name && <div className="validation-error">Provider name is required</div>}
              {!formData.provider.abn && <div className="validation-error">ABN is required</div>}
            </div>

            <div className="provider-preview-actions">
              <button className="btn btn-secondary" onClick={() => setEditingProvider(false)}>
                Done
              </button>
            </div>
              </>
            )}
          </div>
        )}

        {currentStep === 4 && (
          <div className="step-content">
            <h2>Step 4: Support Details</h2>
            <p>Search and select NDIS support items for this agreement.</p>

            <div className="support-item-search" ref={searchSectionRef}>
              {showSearchBar ? (
                <>
                  <label>Add NDIS Support Item</label>
                  <div className="search-container search-container-half">
                    <input
                      type="text"
                      className="form-control search-input-half"
                      value={searchQuery}
                      onChange={(e) => handleSupportSearch(e.target.value)}
                      placeholder="Search NDIS support items..."
                    />

                    {showSearchResults && searchResults.length > 0 && (
                      <div className="search-results">
                        {searchResults.map(item => (
                          <div key={item.item_code} className="search-result-item" onClick={() => addSupportItem(item)}>
                            <div className="item-code">{item.item_code}</div>
                            <div className="item-description">{item.description}</div>
                            <div className="item-price">${item.national_price?.toFixed(2) || 'N/A'} per {item.unit}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showSearchResults && searchResults.length === 0 && searchQuery.length > 2 && (
                      <div className="search-results">
                        <div className="no-results">No support items found for "{searchQuery}"</div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="add-support-item-button-container">
                  <button
                    className="btn btn-success btn-pill"
                    onClick={showAddSupportItemSearch}
                  >
                    + Add Support Item
                  </button>
                </div>
              )}
            </div>

            {formData.supportItems.length > 0 && (
              <div className="selected-support-items">
                <h3>Selected Support Items</h3>
                {formData.supportItems.map(item => (
                  <div key={item.id} className="support-item-card">
                    <div className="item-header">
                      <div className="item-info">
                        <div className="item-code">{item.itemNumber}</div>
                        <div className="item-name">{item.name}</div>
                      </div>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => removeSupportItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="item-details">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Frequency</label>
                          <select
                            className="form-control"
                            value={item.frequency}
                            onChange={(e) => updateSupportItem(item.id, 'frequency', e.target.value)}
                          >
                            <option value="single">Single Session</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="fortnightly">Fortnightly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Hours per Session</label>
                          <input
                            type="number"
                            className="form-control"
                            value={item.hoursPerSession}
                            onChange={(e) => updateSupportItem(item.id, 'hoursPerSession', e.target.value)}
                            step="0.5"
                            placeholder="Enter hours"
                          />
                        </div>
                        {item.frequency !== 'single' && (
                          <div className="form-group">
                            <label>Sessions per {
                              item.frequency === 'daily' ? 'Day' :
                              item.frequency === 'weekly' ? 'Week' :
                              item.frequency === 'fortnightly' ? 'Fortnight' :
                              'Month'
                            }</label>
                            <input
                              type="number"
                              className="form-control"
                              value={item.sessionsPerPeriod}
                              onChange={(e) => updateSupportItem(item.id, 'sessionsPerPeriod', e.target.value)}
                              placeholder="Enter number of sessions"
                            />
                          </div>
                        )}
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Service Location</label>
                          <select
                            className="form-control"
                            value={item.location}
                            onChange={(e) => updateSupportItem(item.id, 'location', e.target.value)}
                          >
                            <option value="Participant home">Participant home</option>
                            <option value="Community">Community</option>
                            <option value="Provider premises">Provider premises</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>

                      {item.location === 'Other' && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>Specify Other Location</label>
                            <input
                              type="text"
                              className="form-control"
                              value={item.otherLocation}
                              onChange={(e) => updateSupportItem(item.id, 'otherLocation', e.target.value)}
                              placeholder="Please specify where the service will take place..."
                            />
                          </div>
                        </div>
                      )}

                      <div className="form-group">
                        <label>Special Requirements</label>
                        <textarea
                          className="form-control"
                          value={item.specialRequirements}
                          onChange={(e) => updateSupportItem(item.id, 'specialRequirements', e.target.value)}
                          placeholder="Any special requirements or notes for this support item..."
                          rows="2"
                        />
                      </div>

                      <div className="pricing-info">
                        <h4>Pricing</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>
                              Rate (NDIS max: ${item.pricing.maxRate?.toFixed(2)})
                              <span className="help-text">Negotiate your own rate up to the NDIS maximum</span>
                            </label>
                            <div className="input-group">
                              <span className="input-prefix">$</span>
                              <input
                                type="number"
                                className="form-control"
                                value={item.pricing.hourlyRate}
                                onChange={(e) => updateSupportItemPricing(item.id, 'hourlyRate', e.target.value)}
                                step="0.01"
                                placeholder="Enter rate"
                              />
                              <span className="input-suffix">per {item.unit}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formData.supportItems.length === 0 && (
              <div className="empty-state">
                <h3>No Support Items Selected</h3>
                <p>Use the search above to find and add NDIS support items to this agreement.</p>
              </div>
            )}

          </div>
        )}

        {currentStep === 5 && (
          <div className="step-content">
            <h2>Step 5: Pricing & Budget</h2>
            <p>Review pricing calculations and estimated costs for all selected support items.</p>

            {formData.supportItems.length === 0 ? (
              <div className="empty-state">
                <h3>No Support Items Selected</h3>
                <p>Go back to Step 4 to add support items before reviewing pricing.</p>
                <button className="btn btn-primary" onClick={() => setCurrentStep(4)}>
                  Go to Step 4
                </button>
              </div>
            ) : (
              <>
                <div className="region-selection">
                  <h3>Service Region</h3>
                  <p>Select the geographic region where services will be delivered (may affect pricing in the future).</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Region</label>
                      <select
                        className="form-control"
                        value={formData.pricing.region}
                        onChange={(e) => handleRegionChange(e.target.value)}
                      >
                        <option value="metro">Metropolitan</option>
                        <option value="regional">Regional</option>
                        <option value="remote">Remote</option>
                        <option value="very-remote">Very Remote</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="cost-breakdown-section">
                  <h3>Support Items & Costs</h3>
                  <div className="support-items-table">
                    {formData.supportItems.map(item => {
                      const costs = calculateItemCosts(item);
                      return (
                        <div key={item.id} className="support-item-cost-row">
                          <div className="item-summary">
                            <div className="item-name">{item.name}</div>
                            <div className="item-details">
                              {item.frequency === 'single' ? (
                                <span>Single session • {item.hoursPerSession || 0} hours • ${item.pricing.hourlyRate || 0}/hour</span>
                              ) : (
                                <span>
                                  {item.sessionsPerPeriod || 1} session{(item.sessionsPerPeriod || 1) > 1 ? 's' : ''} per {item.frequency} •
                                  {item.hoursPerSession || 0} hours each • ${item.pricing.hourlyRate || 0}/hour
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="item-costs">
                            {item.frequency === 'single' ? (
                              <div className="single-cost">
                                <div className="cost-label">One-time cost</div>
                                <div className="cost-value">${costs.costPerSession.toFixed(2)}</div>
                              </div>
                            ) : (
                              <>
                                <div className="cost-item">
                                  <div className="cost-label">Weekly</div>
                                  <div className="cost-value">${costs.weeklyCost.toFixed(2)}</div>
                                </div>
                                <div className="cost-item">
                                  <div className="cost-label">Monthly</div>
                                  <div className="cost-value">${costs.monthlyCost.toFixed(2)}</div>
                                </div>
                                <div className="cost-item">
                                  <div className="cost-label">Annual</div>
                                  <div className="cost-value">${costs.annualCost.toFixed(2)}</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="total-costs-summary">
                  <h3>Total Estimated Costs</h3>
                  <div className="total-costs-grid">
                    {(() => {
                      const totals = calculateTotalCosts();
                      return (
                        <>
                          <div className="total-cost-card weekly">
                            <div className="cost-period">Weekly</div>
                            <div className="cost-amount">${totals.weekly.toFixed(2)}</div>
                          </div>
                          <div className="total-cost-card monthly">
                            <div className="cost-period">Monthly</div>
                            <div className="cost-amount">${totals.monthly.toFixed(2)}</div>
                          </div>
                          <div className="total-cost-card annual">
                            <div className="cost-period">Annual</div>
                            <div className="cost-amount">${totals.annual.toFixed(2)}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="cost-note">
                    <p><strong>Note:</strong> These are estimated costs based on the frequencies and rates specified. Actual costs may vary based on service delivery requirements.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === 6 && (
          <div className="step-content">
            <h2>Step 6: Terms & Clauses</h2>
            <p>Define agreement periods, select legal clauses, and set service terms.</p>

            <div className="agreement-period-section">
              <h3>Agreement Period</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.period.startDate}
                    onChange={(e) => handleAgreementPeriodChange('startDate', e.target.value)}
                  />
                </div>
                {!formData.period.ongoingUntilCancelled && (
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.period.endDate}
                      onChange={(e) => handleAgreementPeriodChange('endDate', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Agreement Duration</label>
                  <div className="duration-options">
                    <button
                      type="button"
                      className={`btn btn-pill ${formData.period.ongoingUntilCancelled ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => handleAgreementPeriodChange('ongoingUntilCancelled', !formData.period.ongoingUntilCancelled)}
                    >
                      No Fixed End Date
                    </button>
                    <span className="duration-help">
                      {formData.period.ongoingUntilCancelled
                        ? 'Agreement will continue until cancelled by either party'
                        : 'Click to make this an ongoing agreement without a fixed end date'
                      }
                    </span>
                  </div>
                </div>
              </div>

            </div>

            <div className="terms-section">
              <h3>Agreement Terms</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Payment Terms</label>
                  <select
                    className="form-control"
                    value={formData.terms.paymentTerms}
                    onChange={(e) => handleTermsChange('paymentTerms', e.target.value)}
                  >
                    <option value="Monthly in arrears">Monthly in arrears</option>
                    <option value="Weekly in arrears">Weekly in arrears</option>
                    <option value="Fortnightly in arrears">Fortnightly in arrears</option>
                    <option value="Upon service delivery">Upon service delivery</option>
                    <option value="Monthly in advance">Monthly in advance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Cancellation Notice Period</label>
                  <select
                    className="form-control"
                    value={formData.terms.cancellationNotice}
                    onChange={(e) => handleTermsChange('cancellationNotice', e.target.value)}
                  >
                    <option value="24 hours">24 hours</option>
                    <option value="48 hours">48 hours</option>
                    <option value="7 days">7 days</option>
                    <option value="14 days">14 days</option>
                    <option value="30 days">30 days</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="clause-selection">
              <h3>Standard Legal Clauses</h3>
              <p>Select the clauses that apply to this service agreement. All selected clauses will be included in the final PDF.</p>

              <div className="clauses-grid">
                {Object.entries(STANDARD_CLAUSES).map(([clauseId, clause]) => {
                  const isExpanded = expandedClauses[clauseId];
                  const showReadMore = clause.content.length > 150;

                  return (
                    <div key={clauseId} className="clause-card">
                      <div className="clause-header">
                        <label className="clause-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.terms.selectedClauses.includes(clauseId)}
                            onChange={() => toggleClause(clauseId)}
                          />
                          <span className="clause-title">{clause.title}</span>
                          {clause.required && <span className="recommended-badge">Recommended</span>}
                        </label>
                      </div>
                      <div className="clause-content">
                        <p className="clause-preview">
                          {isExpanded
                            ? clause.content
                            : clause.content.substring(0, 150) + (showReadMore ? '...' : '')
                          }
                        </p>
                        {showReadMore && (
                          <button
                            type="button"
                            className="btn btn-small btn-info"
                            onClick={() => toggleClauseExpansion(clauseId)}
                          >
                            {isExpanded ? 'Read Less' : 'Read More'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="custom-clauses-section">
              <h3>Custom Clauses</h3>
              <p>Add any additional clauses specific to this agreement.</p>

              <div className="custom-clauses-list">
                {formData.terms.customClauses.map((clause, index) => (
                  <div key={index} className="custom-clause-item">
                    <div className="custom-clause-header">
                      <h4>{typeof clause === 'string' ? `Custom Clause ${index + 1}` : clause.title}</h4>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => removeCustomClause(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="custom-clause-text">
                      {typeof clause === 'string' ? clause : clause.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="custom-clause-editor">
                <h4>Add New Custom Clause</h4>
                <div className="form-group">
                  <label>Clause Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customClauseEditor.title}
                    onChange={(e) => setCustomClauseEditor(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter clause title..."
                  />
                </div>
                <div className="form-group">
                  <label>Clause Content</label>
                  <textarea
                    className="form-control"
                    value={customClauseEditor.content}
                    onChange={(e) => setCustomClauseEditor(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter clause content..."
                    rows="4"
                  />
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={addCustomClause}
                  disabled={!customClauseEditor.title.trim() || !customClauseEditor.content.trim()}
                >
                  Add Custom Clause
                </button>
              </div>
            </div>

            <div className="clauses-summary">
              <h3>Selected Clauses Summary</h3>
              <div className="selected-count">
                {formData.terms.selectedClauses.length} standard clause{formData.terms.selectedClauses.length !== 1 ? 's' : ''} selected
                {formData.terms.customClauses.length > 0 && (
                  <span> + {formData.terms.customClauses.length} custom clause{formData.terms.customClauses.length !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 7 && (
          <div className="step-content">
            <h2>Step 7: Review & Generate</h2>
            <p>Review all entered information before generating your NDIS Service Agreement.</p>

            <div className="review-sections">
              {/* Participant Information */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Participant Information</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(1)}>
                    Edit
                  </button>
                </div>
                {formData.participant ? (
                  <div className="review-content">
                    <div className="review-row">
                      <span>Name:</span>
                      <span>{formData.participant.name}</span>
                    </div>
                    <div className="review-row">
                      <span>NDIS Number:</span>
                      <span>{formData.participant.ndisNumber || 'Not provided'}</span>
                    </div>
                    <div className="review-row">
                      <span>Date of Birth:</span>
                      <span>{formData.participant.dateOfBirth || 'Not provided'}</span>
                    </div>
                    <div className="review-row">
                      <span>Contact:</span>
                      <span>{formData.participant.contact.phone || 'N/A'} | {formData.participant.contact.email || 'N/A'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="review-missing">No participant selected</div>
                )}
              </div>

              {/* Template Information */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Agreement Template</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(2)}>
                    Edit
                  </button>
                </div>
                {formData.template ? (
                  <div className="review-content">
                    <div className="review-row">
                      <span>Template:</span>
                      <span>{formData.template.name}</span>
                    </div>
                    <div className="review-row">
                      <span>Category:</span>
                      <span>{formData.template.category}</span>
                    </div>
                    <div className="review-row">
                      <span>Description:</span>
                      <span>{formData.template.description}</span>
                    </div>
                  </div>
                ) : (
                  <div className="review-missing">No template selected</div>
                )}
              </div>

              {/* Provider Information */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Provider Information</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(3)}>
                    Edit
                  </button>
                </div>
                <div className="review-content">
                  <div className="review-row">
                    <span>Name:</span>
                    <span>{formData.provider.name || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span>ABN:</span>
                    <span>{formData.provider.abn || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span>Contact Person:</span>
                    <span>{formData.provider.contactPerson || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span>Phone:</span>
                    <span>{formData.provider.phone || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span>Email:</span>
                    <span>{formData.provider.email || 'Not provided'}</span>
                  </div>
                  <div className="review-row">
                    <span>Address:</span>
                    <span>
                      {[
                        formData.provider.address.street,
                        formData.provider.address.suburb,
                        formData.provider.address.state,
                        formData.provider.address.postcode
                      ].filter(Boolean).join(', ') || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Support Items */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Support Items ({formData.supportItems.length})</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(4)}>
                    Edit
                  </button>
                </div>
                {formData.supportItems.length > 0 ? (
                  <div className="review-content">
                    {formData.supportItems.map(item => (
                        <div key={item.id} className="support-item-review">
                          <div className="item-name-code">
                            <span className="item-name">{item.name}</span>
                            <span className="item-code">({item.itemNumber})</span>
                          </div>
                          <div className="item-details">
                            <span>
                              {item.frequency === 'single' ? 'Single session' : `${item.sessionsPerPeriod} session(s) per ${item.frequency}`} •
                              {item.hoursPerSession} hour(s) • ${item.pricing.hourlyRate}/hour •
                              Location: {item.location === 'Other' ? item.otherLocation : item.location}
                            </span>
                          </div>
                          {item.specialRequirements && (
                            <div className="item-requirements">
                              <strong>Special Requirements:</strong> {item.specialRequirements}
                            </div>
                          )}
                        </div>
                    ))}
                  </div>
                ) : (
                  <div className="review-missing">No support items selected</div>
                )}
              </div>

              {/* Pricing Summary */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Pricing Summary</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(5)}>
                    Edit
                  </button>
                </div>
                <div className="review-content">
                  <div className="review-row">
                    <span>Region:</span>
                    <span className="capitalize">{formData.pricing.region.replace('-', ' ')}</span>
                  </div>
                  {(() => {
                    const totals = calculateTotalCosts();
                    return (
                      <>
                        <div className="review-row">
                          <span>Estimated Weekly Cost:</span>
                          <span className="cost-highlight">${totals.weekly.toFixed(2)}</span>
                        </div>
                        <div className="review-row">
                          <span>Estimated Monthly Cost:</span>
                          <span className="cost-highlight">${totals.monthly.toFixed(2)}</span>
                        </div>
                        <div className="review-row">
                          <span>Estimated Annual Cost:</span>
                          <span className="cost-highlight">${totals.annual.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Terms & Clauses */}
              <div className="review-section">
                <div className="review-header">
                  <h3>Terms & Clauses</h3>
                  <button className="btn btn-small btn-info" onClick={() => setCurrentStep(6)}>
                    Edit
                  </button>
                </div>
                <div className="review-content">
                  <div className="review-row">
                    <span>Agreement Period:</span>
                    <span>
                      {formData.period.startDate || 'Not set'} to {formData.period.endDate || 'Not set'}
                    </span>
                  </div>
                  <div className="review-row">
                    <span>Review Date:</span>
                    <span>{formData.period.reviewDate || 'Not set'}</span>
                  </div>
                  <div className="review-row">
                    <span>Payment Terms:</span>
                    <span>{formData.terms.paymentTerms}</span>
                  </div>
                  <div className="review-row">
                    <span>Cancellation Notice:</span>
                    <span>{formData.terms.cancellationNotice}</span>
                  </div>
                  <div className="review-row">
                    <span>Standard Clauses:</span>
                    <span>
                      {formData.terms.selectedClauses.length} clause{formData.terms.selectedClauses.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  {formData.terms.customClauses.length > 0 && (
                    <div className="review-row">
                      <span>Custom Clauses:</span>
                      <span>{formData.terms.customClauses.length} custom clause{formData.terms.customClauses.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Generation Actions */}
            <div className="generation-section">
              <h3>Generate Agreement</h3>
              <p>Your service agreement is ready to be generated. Choose an action below:</p>

              <div className="generation-actions">
                <button className="btn btn-success btn-large" onClick={handleGeneratePDF}>
                  <span className="btn-icon">📄</span>
                  Generate PDF Agreement
                </button>

                <button className="btn btn-secondary" onClick={handleSaveDraft}>
                  <span className="btn-icon">💾</span>
                  Save as Draft
                </button>

                <button className="btn btn-info" disabled>
                  <span className="btn-icon">✉️</span>
                  Send for Signature (Coming Soon)
                </button>
              </div>

              <div className="agreement-preview-note">
                <p><strong>Agreement Number:</strong> {formData.agreementNumber || 'Will be assigned on save'}</p>
                <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                <p><strong>Status:</strong> {formData.status || 'New'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          className="btn btn-secondary"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Previous
        </button>

        <span className="step-counter">
          Step {currentStep} of {totalSteps}
        </span>

        <div style={{ display: 'flex', gap: '10px' }}>
          {currentStep === 4 && !showSearchBar && (
            <button
              className="btn btn-success btn-pill"
              onClick={showAddSupportItemSearch}
            >
              + Add Another Support Item
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={nextStep}
            disabled={currentStep === totalSteps}
          >
            {currentStep === 4 ? "I'm Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}